import { useState } from 'react';
import { useLocation } from 'wouter';
import { CalendarDays, Plus } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EventList } from '@/components/schedule/EventList';
import { ImportBottomSheet } from '@/components/schedule/ImportBottomSheet';
import { useInitWedding } from '@/hooks/useWedding';
import { useEvents, useDeleteEvent, useUpdateEventStatus } from '@/hooks/useEvents';
import { useToast } from '@/hooks/useToast';
import type { EventStatus } from '@/lib/types';

export function SchedulePage() {
  const [, navigate] = useLocation();
  const { weddingId, isCreating } = useInitWedding();
  const { data: events, isLoading: eventsLoading } = useEvents(weddingId);
  const deleteEvent = useDeleteEvent(weddingId ?? '');
  const updateStatus = useUpdateEventStatus(weddingId ?? '');
  const { toast } = useToast();

  const [importSheetOpen, setImportSheetOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null); // eventId to delete

  const isLoading = isCreating || eventsLoading;
  const hasEvents = (events?.length ?? 0) > 0;

  function handleDelete(eventId: string) {
    setDeleteConfirm(eventId);
  }

  function handleConfirmDelete() {
    if (!deleteConfirm) return;
    deleteEvent.mutate(deleteConfirm, {
      onSuccess: () => toast('Event deleted', 'success'),
      onError: () => toast('Failed to delete event', 'error'),
    });
    setDeleteConfirm(null);
  }

  function handleStatusChange(eventId: string, status: EventStatus) {
    updateStatus.mutate({ eventId, status }, {
      onSuccess: () => toast(`Event marked as ${status}`, 'success'),
      onError: () => toast('Failed to update event', 'error'),
    });
  }

  function handleEdit(eventId: string) {
    navigate(`/schedule/event/${eventId}/edit`);
  }

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--color-background)' }}>
      <Header
        title={hasEvents && weddingId ? 'Schedule' : 'Schedule'}
        rightAction={
          hasEvents ? (
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full"
              style={{ background: 'var(--color-primary-muted)', color: 'var(--color-primary)' }}
            >
              {events?.length ?? 0}
            </span>
          ) : undefined
        }
      />

      <main className="flex-1 pb-24">
        {isLoading && <LoadingSkeleton lines={4} />}

        {!isLoading && !hasEvents && (
          <EmptyState
            icon={CalendarDays}
            title="No timeline loaded"
            description="Import a planner's timeline or add events manually to get started."
            action={
              <button
                className="px-6 py-3 rounded-xl text-sm font-semibold touch-target"
                style={{
                  background: 'var(--color-primary)',
                  color: '#1A1A2E',
                }}
                onClick={() => setImportSheetOpen(true)}
              >
                Import Timeline
              </button>
            }
          />
        )}

        {!isLoading && hasEvents && events && (
          <EventList
            events={events}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onEdit={handleEdit}
          />
        )}
      </main>

      {/* FAB */}
      <button
        className="fixed z-40 flex items-center justify-center rounded-full shadow-lg"
        style={{
          bottom: '80px',
          right: '20px',
          width: '56px',
          height: '56px',
          background: 'var(--color-primary)',
          color: '#1A1A2E',
          boxShadow: 'var(--shadow-fab)',
        }}
        onClick={() => setImportSheetOpen(true)}
        aria-label="Add events"
      >
        <Plus size={24} strokeWidth={2.5} />
      </button>

      <ImportBottomSheet
        open={importSheetOpen}
        onOpenChange={setImportSheetOpen}
      />

      <ConfirmDialog
        open={deleteConfirm !== null}
        onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}
        title="Delete Event"
        description="This event will be permanently removed from your timeline."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
